import { createElement, delegate } from '../../../core/dom/helpers.js';

let idCounter = 0;

/**
 * Mounts an accordion field section layout.
 *
 * @type {import('../../../contracts/field-sections.js').FieldSectionLayoutMount}
 */
export function mount(container, config) {
  const uid = idCounter++;
  const { sections, sectionFields, unsectionedFields } = config;

  // --- Build DOM ---
  const root = createElement('div', { class: 'field-sections field-sections--accordion' });

  /** @type {Map<string, HTMLButtonElement>} */
  const triggers = new Map();
  /** @type {Map<string, HTMLElement>} */
  const panels = new Map();

  for (const sec of sections) {
    const triggerId = `fsah-${uid}-${sec.key}`;
    const panelId = `fsa-${uid}-${sec.key}`;

    const label = createElement('span', { class: 'field-sections-accordion-label' }, [sec.label]);
    const icon = createElement('span', { class: 'field-sections-accordion-icon', 'aria-hidden': 'true' });

    const trigger = /** @type {HTMLButtonElement} */ (createElement('button', {
      type: 'button',
      id: triggerId,
      'aria-expanded': 'true',
      'aria-controls': panelId,
      class: 'field-sections-accordion-trigger',
    }, [label, icon]));
    triggers.set(sec.key, trigger);

    const heading = createElement('h3', { class: 'field-sections-accordion-heading' }, [trigger]);

    const panel = createElement('div', {
      role: 'region',
      id: panelId,
      'aria-labelledby': triggerId,
      class: 'field-sections-accordion-panel',
    });

    // Move field wrappers into panel
    const wrappers = sectionFields.get(sec.key) ?? [];
    for (const wrapper of wrappers) {
      panel.appendChild(wrapper);
    }
    panels.set(sec.key, panel);

    const item = createElement('div', { class: 'field-sections-accordion-item' }, [heading, panel]);
    root.appendChild(item);
  }

  // Unsectioned fields stay in container before the section layout
  container.appendChild(root);

  // --- Toggle ---
  /**
   * @param {string} key
   */
  function toggle(key) {
    const trigger = triggers.get(key);
    const panel = panels.get(key);
    if (!trigger || !panel) return;

    const expanded = trigger.getAttribute('aria-expanded') === 'true';
    trigger.setAttribute('aria-expanded', String(!expanded));
    panel.hidden = expanded;
  }

  // --- Event delegation ---
  const cleanupClick = delegate(root, 'click', '.field-sections-accordion-trigger', (_event, target) => {
    const key = sectionKeyFromTrigger(target);
    if (key) toggle(key);
  });

  /**
   * @param {Element} triggerEl
   * @returns {string | null}
   */
  function sectionKeyFromTrigger(triggerEl) {
    for (const [key, btn] of triggers) {
      if (btn === triggerEl) return key;
    }
    return null;
  }

  // --- Handle ---
  return {
    activateSection(key) {
      const trigger = triggers.get(key);
      if (!trigger) return;
      if (trigger.getAttribute('aria-expanded') !== 'true') {
        toggle(key);
      }
    },

    getActiveSection() {
      // Accordion has no single active section (multiple can be open)
      return null;
    },

    destroy() {
      // Remove listeners
      cleanupClick();

      // Restore field wrappers to container in deterministic normalized order
      for (const wrapper of unsectionedFields) {
        container.appendChild(wrapper);
      }
      for (const sec of sections) {
        const wrappers = sectionFields.get(sec.key) ?? [];
        for (const wrapper of wrappers) {
          container.appendChild(wrapper);
        }
      }

      // Remove layout chrome
      root.remove();
    },
  };
}
